import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TEMA, useMobil } from '../theme';
import { useAuth } from '../context/AuthContext';
import { RestaurangStartsida as Data, hämtaRestaurangStartsida } from '../api/client';
import { sida, rubrik, etikett, länkknapp } from '../components/inkopStil';
import { hälsningsfras } from './Idag';
import { ROSA, mono, nyckeltal, rad, ellips, Ruta, Läge, Rutnät, Personväljare } from '../components/Oversikt';

// Startsidan i Restaurang är en översikt, inte en arbetsyta (beslut 3 nov 2026).
// Listan med avbockning och snabbtillägg ligger på sidan Att göra (/att-gora).
//
//   Att göra                  i dag och försenat, de tre äldsta
//   Försäljning               fakturerat hittills i månaden mot samma dagar förra månaden
//   Provat, inte beställt     kunder med viner de gillat de senaste sex veckorna, utan order
//   Kunder att höra av sig    köpte under året men inte på länge, och inget inplanerat
//   Lager som tar slut        de kritiska vinerna, så att inget lovas bort som inte finns

const MÅNAD_KORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const MÅNAD_LÅNG = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];

const tkr = (v: number) => (Math.abs(v) >= 1_000_000
  ? `${(v / 1_000_000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} Mkr`
  : `${Math.round(v / 1000).toLocaleString('sv-SE')} tkr`);
const kronor = (v: number) => `${Math.round(v).toLocaleString('sv-SE')} kr`;
const kortDatum = (d: string) => `${Number(d.slice(8, 10))} ${MÅNAD_KORT[Number(d.slice(5, 7)) - 1]}`;

function AttGöraRuta({ d, spann }: { d: Data; spann: boolean }) {
  const a = d.att_gora;
  const läge = a.antal === 0
    ? (a.kommande_14 ? `Inget i dag. ${a.kommande_14} inplanerade de närmaste två veckorna.` : 'Inget att göra just nu.')
    : [a.forsenade ? `${a.forsenade} ${a.forsenade === 1 ? 'försenad' : 'försenade'}` : null,
      a.antal - a.forsenade ? `${a.antal - a.forsenade} i dag` : null,
      a.imorgon ? `${a.imorgon} i morgon` : null].filter(Boolean).join(' · ');
  return (
    <Ruta titel="Att göra" länk="/att-gora" länktext="Att göra" spann={spann}>
      <div style={nyckeltal}>{a.antal}</div>
      <Läge text={läge} ton={a.forsenade ? 'varning' : a.antal === 0 ? 'bra' : undefined} />
      {a.poster.map((p, n) => (
        <Link key={n} to={p.kund_id ? `/kund/${p.kund_id}` : '/att-gora'} style={{ ...rad, alignItems: 'center' }}>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ ...ellips, display: 'block', color: TEMA.ink }}>{p.titel}</span>
            {p.detalj && <span style={{ ...ellips, display: 'block', fontSize: '12px', color: TEMA.inkFaint, marginTop: '1px' }}>{p.detalj}</span>}
          </span>
          <span style={{ fontSize: '12px', whiteSpace: 'nowrap', color: p.dagar_sen > 0 ? ROSA : TEMA.inkSoft }}>
            {p.dagar_sen === 0 ? 'i dag' : p.dagar_sen === 1 ? '1 dag sen' : `${p.dagar_sen} dagar sen`}
          </span>
        </Link>
      ))}
      {a.antal > a.poster.length && (
        <Link to="/att-gora" style={{ ...länkknapp, fontSize: '12.5px', textDecoration: 'none', paddingTop: '10px', borderTop: `1px solid ${TEMA.border}` }}>
          Visa alla {a.antal}
        </Link>
      )}
    </Ruta>
  );
}

function FörsäljningRuta({ d }: { d: Data }) {
  const f = d.forsaljning;
  const förändring = f.forra_samma_period ? Math.round(((f.hittills - f.forra_samma_period) / f.forra_samma_period) * 100) : null;
  const nr = Number(f.manad.slice(5, 7)) - 1;
  const förraNr = (nr + 11) % 12;
  const dag = new Date().getDate();
  const hela = f.manader.slice(0, -1);
  const snitt = hela.length ? hela.reduce((x, m) => x + m.netto, 0) / hela.length : 0;
  const max = Math.max(f.hittills, f.forra_samma_period, 1);
  // Samma antal dagar in i varje månad, så att en månad som just börjat jämförs rättvist.
  const stapel = (etikett: string, värde: number, nu: boolean) => (
    <div style={{ display: 'grid', gridTemplateColumns: '78px minmax(0, 1fr) 64px', alignItems: 'center', gap: '10px', fontSize: '12px', marginTop: '6px' }}>
      <span style={{ color: nu ? TEMA.ink : TEMA.inkFaint }}>{etikett}</span>
      <span style={{ height: '8px', background: TEMA.border, borderRadius: '2px' }}>
        <span style={{ display: 'block', height: '100%', width: `${(värde / max) * 100}%`, background: nu ? TEMA.accent : 'rgba(155,154,147,0.45)', borderRadius: '2px' }} />
      </span>
      <span style={{ ...mono, textAlign: 'right', color: nu ? TEMA.ink : TEMA.inkSoft }}>{tkr(värde)}</span>
    </div>
  );
  return (
    <Ruta titel="Försäljning" länk="/statistik" länktext="Statistik">
      <div style={nyckeltal}>{tkr(f.hittills)}</div>
      <Läge
        text={<>fakturerat i {MÅNAD_LÅNG[nr]}, exkl. moms{förändring != null && (
          <span style={{ color: förändring >= 0 ? TEMA.positive : ROSA }}> · {förändring >= 0 ? '+' : ''}{förändring} %</span>
        )}</>}
      />
      {stapel(`1–${dag} ${MÅNAD_KORT[nr]}`, f.hittills, true)}
      {stapel(`1–${dag} ${MÅNAD_KORT[förraNr]}`, f.forra_samma_period, false)}
      <div style={{ ...rad, marginTop: '14px', color: TEMA.inkSoft, fontSize: '12.5px' }}>
        <span>Hela {MÅNAD_LÅNG[förraNr]}</span>
        <span style={{ ...mono, color: TEMA.ink }}>{tkr(hela[hela.length - 1]?.netto || 0)}</span>
      </div>
      <div style={{ ...rad, color: TEMA.inkSoft, fontSize: '12.5px' }}>
        <span>Snitt per månad, {hela.length} mån</span>
        <span style={{ ...mono, color: TEMA.ink }}>{tkr(snitt)}</span>
      </div>
    </Ruta>
  );
}

function ProvatRuta({ d }: { d: Data }) {
  const p = d.provat;
  return (
    <Ruta titel="Provat, inte beställt">
      <div style={nyckeltal}>{p.antal}</div>
      <Läge text={p.antal ? `gillade viner hos ${p.kunder} ${p.kunder === 1 ? 'kund' : 'kunder'}, utan order` : `Allt som gillats de senaste ${p.dagar / 7} veckorna är beställt.`}
        ton={p.antal ? undefined : 'bra'} />
      {p.poster.map(x => (
        <Link key={x.kund_id} to={`/kund/${x.kund_id}`} style={rad}>
          <span style={{ minWidth: 0 }}>
            <span style={{ ...ellips, display: 'block', color: TEMA.ink }}>{x.kund}</span>
            <span style={{ ...ellips, display: 'block', fontSize: '12px', color: TEMA.inkFaint }}>
              {x.viner.join(', ')}{x.antal_viner > x.viner.length ? ` och ${x.antal_viner - x.viner.length} till` : ''}
            </span>
          </span>
          <span style={{ fontSize: '12px', whiteSpace: 'nowrap', color: x.dagar_kvar <= 7 ? ROSA : TEMA.inkSoft }}>{x.dagar_kvar} d kvar</span>
        </Link>
      ))}
    </Ruta>
  );
}

function VilandeRuta({ d }: { d: Data }) {
  const v = d.vilande;
  return (
    <Ruta titel="Kunder att höra av sig till" länk="/alla-kunder" länktext="Kunder">
      <div style={nyckeltal}>{v.antal}</div>
      <Läge text={v.antal ? `köpt i år, inte på ${v.dagar} dagar, inget inplanerat` : 'Alla kunder har köpt nyligen eller har något inplanerat.'}
        ton={v.antal ? undefined : 'bra'} />
      {v.poster.map(x => (
        <Link key={x.kund_id} to={`/kund/${x.kund_id}`} style={rad}>
          <span style={{ minWidth: 0 }}>
            <span style={{ ...ellips, display: 'block', color: TEMA.ink }}>{x.kund}</span>
            <span style={{ ...ellips, display: 'block', fontSize: '12px', color: TEMA.inkFaint }}>senast {kortDatum(x.senast)} · {x.dagar} dagar sen</span>
          </span>
          <span style={{ ...mono, fontSize: '12.5px', color: TEMA.inkSoft, whiteSpace: 'nowrap' }}>{tkr(x.ar_netto)}/år</span>
        </Link>
      ))}
    </Ruta>
  );
}

function LagerRuta({ d }: { d: Data }) {
  const l = d.lager;
  return (
    <Ruta titel="Lager som tar slut" länk="/lager" länktext="Lågt lagersaldo">
      <div style={nyckeltal}>{l.kritiska}</div>
      <Läge
        text={l.kritiska ? `viner med högst ${l.kritisk_max} flaskor kvar${l.antal > l.kritiska ? `, ${l.antal - l.kritiska} till på väg ner` : ''}`
          : l.antal ? `inga kritiska, ${l.antal} att bevaka` : 'Inga viner under bevakningsnivån.'}
        ton={l.kritiska ? 'varning' : 'bra'}
      />
      {l.poster.map(x => (
        <div key={x.artikel_nr} style={rad}>
          <span style={{ minWidth: 0 }}>
            <span style={{ ...ellips, display: 'block', color: TEMA.ink }}>{x.namn}</span>
            <span style={{ display: 'block', fontSize: '12px', color: TEMA.inkFaint }}>{kronor(x.pris)}</span>
          </span>
          <span style={{ ...mono, fontSize: '12.5px', color: ROSA, whiteSpace: 'nowrap' }}>{x.saldo} fl</span>
        </div>
      ))}
    </Ruta>
  );
}

export default function RestaurangStartsida() {
  const ärMobil = useMobil();
  const { profil } = useAuth();
  const [d, setD] = useState<Data | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const [person, setPerson] = useState('');
  const [laddar, setLaddar] = useState(false);
  const senaste = useRef(0);

  const ladda = useCallback(() => {
    const nr = ++senaste.current;
    setLaddar(true);
    hämtaRestaurangStartsida(person || undefined)
      .then(r => { if (nr === senaste.current) { setD(r.data); setFel(null); } })
      .catch(() => { if (nr === senaste.current) setFel('Kunde inte hämta översikten just nu. Försök igen om en stund.'); })
      .finally(() => { if (nr === senaste.current) setLaddar(false); });
  }, [person]);

  useEffect(() => { ladda(); }, [ladda]);

  const vald = person || (d ? (d.person ?? 'alla') : '');
  const datum = new Date().toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' }).toUpperCase();

  return (
    <div style={sida}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div>
          <span style={etikett}>{datum}</span>
          <h1 style={rubrik}>
            {d && d.person !== (profil?.namn || null)
              ? <>Restaurang startsida<span style={{ color: TEMA.inkFaint, fontSize: '18px' }}> · {d.person || 'alla säljare'}</span></>
              : <>{hälsningsfras()}{profil?.namn ? `, ${profil.namn}` : ''}</>}
            {laddar && d && <span role="status" style={{ marginLeft: '12px', color: TEMA.inkFaint, fontSize: '13px', fontFamily: "'Inter', sans-serif" }}>Laddar...</span>}
          </h1>
        </div>
        {d?.admin && d.personer.length > 0 && (
          <Personväljare val={[...d.personer.map(n => ({ id: n, namn: n })), { id: 'alla', namn: 'Alla' }]} vald={vald} onVälj={setPerson} />
        )}
      </div>

      {fel && <p style={{ color: ROSA, fontSize: '13px' }}>{fel}</p>}
      {!d && !fel && <p>Laddar...</p>}

      {d && (
        <Rutnät ärMobil={ärMobil} laddar={laddar}>
          <AttGöraRuta d={d} spann={!ärMobil} />
          <FörsäljningRuta d={d} />
          <ProvatRuta d={d} />
          <VilandeRuta d={d} />
          <LagerRuta d={d} />
        </Rutnät>
      )}
    </div>
  );
}
